import { useEffect, useRef, useState } from 'react';
import type { Attachment, AttachmentCategory } from '../types';
import { ATTACHMENT_CATEGORY_LABELS, ATTACHMENT_CATEGORY_ORDER } from '../types';
import * as db from '../lib/db';
import type { AttachmentParent } from '../lib/db';

function fileIcon(contentType: string | null): string {
  if (!contentType) return '📎';
  if (contentType.includes('pdf')) return '📄';
  if (contentType.startsWith('image/')) return '🖼️';
  return '📎';
}

export default function AttachmentsSection({ parent }: { parent: AttachmentParent }) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState<AttachmentCategory>('other');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const parentKey = JSON.stringify(parent);

  async function refresh() {
    setLoading(true);
    try {
      setAttachments(await db.listAttachments(parent));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentKey]);

  async function handleFilesSelected(files: FileList | null) {
    if (!files || !files.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await db.uploadAttachment(parent, file, category);
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleOpen(attachment: Attachment) {
    try {
      const url = await db.getAttachmentUrl(attachment.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleDelete(attachment: Attachment) {
    if (!confirm(`Delete "${attachment.file_name}"?`)) return;
    try {
      await db.deleteAttachment(attachment);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const grouped = ATTACHMENT_CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: attachments.filter((a) => a.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <section className="attachments-section">
      <div className="section-header">
        <h2>Documents</h2>
        <div className="attachments-upload">
          <select
            className="category-select"
            value={category}
            onChange={(e) => setCategory(e.target.value as AttachmentCategory)}
            aria-label="Document category"
          >
            {ATTACHMENT_CATEGORY_ORDER.map((cat) => (
              <option key={cat} value={cat}>
                {ATTACHMENT_CATEGORY_LABELS[cat]}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary small"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : '+ Add'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => handleFilesSelected(e.target.files)}
          />
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : attachments.length === 0 ? (
        <p className="muted empty">
          No documents yet. Pick a category above, then add an insurance policy, title,
          registration, contract, receipt, or anything else worth keeping.
        </p>
      ) : (
        <div className="attachment-groups">
          {grouped.map((g) => (
            <div key={g.category} className="attachment-group">
              <h3 className="attachment-group-label">{ATTACHMENT_CATEGORY_LABELS[g.category]}</h3>
              <ul className="attachment-list">
                {g.items.map((a) => (
                  <li key={a.id} className="attachment-row">
                    <button
                      type="button"
                      className="attachment-open"
                      onClick={() => handleOpen(a)}
                    >
                      <span className="emoji">{fileIcon(a.content_type)}</span>
                      <span className="attachment-name">{a.file_name}</span>
                    </button>
                    <button
                      type="button"
                      className="attachment-delete"
                      onClick={() => handleDelete(a)}
                      aria-label={`Delete ${a.file_name}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
